import React, { useEffect, useState } from "react";
import { Box, makeStyles } from "@material-ui/core";
import OpeningHours from "../../../../../../models/OpeningHours";
import {
  OpeningHoursAttributes,
  OpeningHoursMessageAttributes,
} from "../../../../../../attributes/opening-hours-attributes";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import OpeningHoursService from "../../../../../../services/OpeningHoursService";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import Alert from "@material-ui/lab/Alert";
import MenuItem from "@material-ui/core/MenuItem";
import { InputField } from "../../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../../components/input-button/InputButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Modal from "@material-ui/core/Modal";
import BusinessBranchService from "../../../../../../services/BusinessBranchService";
import { InputSelect } from "../../../../../../components/input-select/InputSelect";

interface AddOpeningHoursProps {
  branch: number;
  isOpened: boolean;
  closeCallback: (succeed: boolean) => void;
  onSaveOpeningHour: (openingHours: OpeningHours) => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
  },
  modalBody: {
    width: 470,
    padding: 10,
    border: "none",
  },
}));

export const AddOpeningHours = (props: AddOpeningHoursProps) => {
  const classes = useStyles();
  const { branch, isOpened, closeCallback } = props;
  const [openingHours, setOpeningHours] = useState(
    (): Array<OpeningHours> => []
  );
  const [openingHoursMessages, setOpeningHoursMessages] = useState(
    (): Array<OpeningHoursMessageAttributes> => []
  );
  const { formatMessage } = useIntl();
  const [error, setError] = useState(false);
  const intl = useIntl();
  const [succeed, setSucceed] = useState(false);

  const initFormValues = (): OpeningHoursAttributes => {
    return {
      day_id: 1,
      opening_hour: "",
      closing_hour: "",
    };
  };

  const formik = useFormik({
    initialValues: {
      day_id: 1,
      opening_hour: "",
      closing_hour: "",
    },
    validationSchema: Yup.object().shape({
      message: Yup.string().required(
        formatMessage({ id: "sharedScreen.selectDayOpeningHour" })
      ),
    }),
    onSubmit: async (values, { setSubmitting }: FormikHelpers<any>) => {
      setError(false);
      try {
        let openingHour = OpeningHours.fromJson(formik.values);
        props.onSaveOpeningHour(openingHour);

        const addopeningHourResult = await OpeningHoursService.createOpeningHours(
          {
            branch,
            day_id: +values.day_id,
            opening_hour: +values.opening_hour,
            closing_hour: values.closing_hour,
          }
        );
        if (
          addopeningHourResult.isSuccess &&
          addopeningHourResult.data.code === 100
        ) {
          closeCallback(true);
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  const addOpeningHour = (event: any) => {
    event.preventDefault();
    let openingHour = OpeningHours.fromJson(formik.values);

    props.onSaveOpeningHour(openingHour);
    setSucceed(true);
          setTimeout(() => {
            setSucceed(false);
          }, 1000);
    formik.setValues(initFormValues());
  };

  const handleDayId = (event: any, child: any) => {
    formik.values.day_id = event.target.value;
    let day: string = event.target.value;
    formik.setFieldValue(child, day);
  };
  const getData = () => {
    BusinessBranchService.getOpeningHours(branch).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let openingHours: Array<OpeningHours> = [];
        let openingHoursMessages: Array<OpeningHoursMessageAttributes> = [];
        const data = JSON.parse(JSON.stringify(response.data.data));

        data.hours.forEach((item, index) => {
          openingHours.push(
            OpeningHours.fromJson(item as OpeningHoursAttributes)
          );
        });
        data.messages.forEach((item, index) => {
          openingHoursMessages.push(item);
        });
        setOpeningHours(openingHours);
        setOpeningHoursMessages(openingHoursMessages);
      }
    });
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <Modal
      className={classes.modal}
      open={isOpened}
      onClose={() => closeCallback(false)}
    >
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.addOpeningHours" />
              </Typography>
              <IconButton edge="end" onClick={() => closeCallback(false)}>
                <HighlightOffIcon fontSize="large" />
              </IconButton>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  <FormattedMessage id="sharedScreen.thereWasAProblemAddingTheOpeningHours" />
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <Box mb={2}>
                <InputSelect
                  fullWidth
                  id="day_id"
                  name="day_id"
                  value={formik.values.day_id}
                  label={intl.formatMessage({
                    id: "sharedScreen.dayWeek",
                  })}
                  onChange={handleDayId}
                  error={
                    Boolean(formik.touched.day_id) &&
                    Boolean(formik.errors.day_id)
                  }
                  helperText={formik.touched.day_id && formik.errors.day_id}
                >
                  <MenuItem>
                    <FormattedMessage id="sharedScreen.dayWeek" />
                  </MenuItem>
                  {openingHoursMessages.map((message, index) => {
                    return (
                      <MenuItem value={message.day_id}>
                        {message.message}
                      </MenuItem>
                    );
                  })}
                </InputSelect>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                type="Time"
                value={formik.values.opening_hour}
                fullWidth
                label={formatMessage({ id: "sharedScreen.opening" })}
                name="opening_hour"
                onChange={formik.handleChange}
                error={
                  formik.touched.opening_hour &&
                  Boolean(formik.errors.opening_hour)
                }
                helperText={
                  formik.touched.opening_hour && formik.errors.opening_hour
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                type="Time"
                value={formik.values.closing_hour}
                fullWidth
                label={formatMessage({ id: "sharedScreen.closing" })}
                name="closing_hour"
                onChange={formik.handleChange}
                error={
                  formik.touched.closing_hour &&
                  Boolean(formik.errors.closing_hour)
                }
                helperText={
                  formik.touched.closing_hour && formik.errors.closing_hour
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={12} container direction="row">
              <InputButton
                disabled={formik.isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
                onClick={addOpeningHour}
              >
                <FormattedMessage id="sharedScreen.add" />
                {formik.isSubmitting && <CircularProgress size={30} />}
                {succeed && <CircularProgress color="inherit" size={25} />}
              </InputButton>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
};
export default AddOpeningHours;
